import axios, { AxiosRequestConfig } from "axios";
import { JobSearchRequest } from "@themuse/design-system";
import getConfig from "next/config";

import { formatLog } from "utils/logging";
import { getMuseJobsConfig } from "orm/jobs";
import { getLocationConfig } from "orm/locations";
import { CompanySuggestorResults } from "interfaces/api/suggestor";
import { suggestionApiQueryMaxlength } from "./constants";

/**
 * Request company suggestions.
 */
export const fetchCompanySuggestions = async (inputValue: string) => {
    const { publicRuntimeConfig: { apiBase, apiRequestTimeoutMs, appName } } = getConfig();
    const path = `${apiBase}/companies?query=${inputValue}`;
    try {
        const response = await axios(path, { timeout: apiRequestTimeoutMs });
        return response.data;
    } catch (error) {
        const msg = `${appName} was unable to request company suggestions from ${path}`;
        console.error(msg, JSON.stringify(formatLog({ error })));
        throw new Error(msg);
    }
};

/**
 * Returns the Axios config for requesting Jobs from TheMuse.
 */
export const getCompanyAttributesConfig = (): AxiosRequestConfig => {
    const { serverRuntimeConfig: { companyApiHost, companyApiToken } } = getConfig();
    const { publicRuntimeConfig: { apiRequestTimeoutMs } } = getConfig();
    const path = `${companyApiHost}/api/v1/companies/attributes`;

    const museConfig: AxiosRequestConfig = {
        method: ("GET"),
        url: path,
        timeout: apiRequestTimeoutMs,
        headers: {
            Authorization: `Bearer ${companyApiToken}`
        }
    };

    return museConfig;
};

export const fetchCompanyAttributes = async (): Promise<any> => {
    const { serverRuntimeConfig: { appName } } = getConfig();
    const axiosConfig: AxiosRequestConfig = getCompanyAttributesConfig();
    try {
        const response = await axios(axiosConfig);
        const ormResults = response.data;
        return ormResults;
    } catch (error) {
        const msg = `${appName} was unable to request company attributes from ${axiosConfig.url}`;
        console.error(msg, JSON.stringify(formatLog({ error })));
        throw new Error(msg);
    }
};

/**
 * Request location suggestions.
 */
export const fetchLocationSuggestions = async (inputValue: string) => {
    const { serverRuntimeConfig: { appName } } = getConfig();
    let config;
    if (!inputValue) {
        return null;
    }
    try {
        config = getLocationConfig(inputValue);
        const response = await axios(config);
        return response.data;
    } catch (error) {
        const msg = `${appName} was unable to request locations suggestions from ${config?.url}`;
        console.error(msg, JSON.stringify(formatLog({ error })));
        throw new Error(msg);
    }
};

/**
 * Request job recommendations.
 */
export const fetchJobRecommendations = async () => {
    const { publicRuntimeConfig: { apiBase, apiRequestTimeoutMs, appName } } = getConfig();
    const path = `${apiBase}/recommendations`;
    try {
        const response = await axios.get(path, { timeout: apiRequestTimeoutMs });
        return response.data;
    } catch (error) {
        const msg = `${appName} was unable to request job recommendations from ${path}`;
        console.error(msg, JSON.stringify(formatLog({ error })));
        throw new Error(msg);
    }
};

/**
 * Request jobs directly from the backend API or proxied through the Next.js internal API.
 */
export const fetchJobs = async (queryParams: JobSearchRequest, ctsEnabled: boolean, cookies?: string) => {
    const { publicRuntimeConfig: { appName } } = getConfig();
    const axiosConfig: AxiosRequestConfig = getMuseJobsConfig(queryParams, ctsEnabled, cookies);

    try {
        const response = await axios(axiosConfig);
        return response.data;
    } catch (error) {
        const msg = `${appName} was unable to request job results from ${axiosConfig.url}`;
        console.error(msg, JSON.stringify(formatLog({ error })));
        throw new Error(msg);
    }
};

/**
 * Request company suggestions from the backend API.
 */
export const fetchApiCompanySuggestions = async (query: string): Promise<CompanySuggestorResults> => {
    const { serverRuntimeConfig: { apiRequestTimeoutMs, jobsApiHost, appName } } = getConfig();
    // if query exists we will pass it in the params.
    const queryObj = query ? { query } : {};
    const config = { params: { type: "company_name", ...queryObj }, timeout: apiRequestTimeoutMs };
    const path = `${jobsApiHost}/api/v2/suggestions`;
    try {
        const response = await axios(path, config);
        return response.data;
    } catch (error) {
        const msg = `${appName} was unable to request job suggestions server side from ${path}`;
        console.error(msg, JSON.stringify(formatLog({ error })));
        throw new Error(msg);
    }
};

/**
 * Request locations from the backend API.
 */
export const fetchLocations = async (userInput: string) => {
    const location = userInput?.substring(0, suggestionApiQueryMaxlength);
    const {
        serverRuntimeConfig: { jobsApiHost },
        publicRuntimeConfig: { apiRequestTimeoutMs, appName }
    } = getConfig();
    const config = {
        params: {
            type: "location",
            query: location?.length > 0 && location
        },
        timeout: apiRequestTimeoutMs
    };
    const path = `${jobsApiHost}/api/v2/suggestions`;
    try {
        const response = await axios.get(path, config);
        return response.data;
    } catch (error) {
        const msg = `${appName} was unable to request locations server side from ${path}`;
        console.error(msg, JSON.stringify(formatLog({ error })));
        throw new Error(msg);
    }
};

export const formatLocationResponse = (response: any) => response.map((location: any) => ({
    label: location.text,
    value: location.text,
    latitude: location.value.latitude,
    longitude: location.value.longitude,
    remote_work_location_type: location.value.remote_work_location_type
}));

export const fetchApiLocationSuggestions = async (userInput: string): Promise<any> => {
    const locationsResponse = await fetchLocations(userInput);
    return formatLocationResponse(locationsResponse.data);
};

export const formatCompanySuggestions = (suggestions: CompanySuggestorResults["data"]) => (
    suggestions.map((company) => ({
        label: company.text,
        value: company.value.short_name
    }))
);
